import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import SpendLimit from "@/components/admin/SpendLimit";

const SECTION_ACCENT = { cp: "#e08a2a", bb: "#2A5CE0", ps: "#3BB6D9" };
const TYPES = ["A", "B", "C", "D", "E", "F"];
const dt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function TypeBar({ by_type, total }) {
  return (
    <div className="flex gap-1 mt-2" data-testid="bank-type-bar">
      {TYPES.map((t) => (
        <div key={t} className="flex-1 text-center" data-testid={`bank-type-${t}`}>
          <div className="h-10 rounded-md flex items-end justify-center font-mono text-xs pb-0.5 relative overflow-hidden" style={{ background: "var(--muted)" }}>
            <div className="absolute inset-x-0 bottom-0" style={{ height: `${Math.min(100, (by_type[t] || 0) * 25)}%`, background: "var(--primary)", opacity: 0.3 }} />
            <span className="relative z-10 font-bold">{by_type[t] || 0}</span>
          </div>
          <div className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>{t}</div>
        </div>
      ))}
    </div>
  );
}

function SectionCard({ s, active }) {
  const needed = Math.max(0, s.goal_passages - s.total);
  const w = s.last_24h || {};
  return (
    <div className="pm-card p-5" style={{ borderTop: `3px solid ${SECTION_ACCENT[s.section_id]}` }} data-testid={`bank-section-${s.section_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-xl font-bold">{s.short}</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>{s.name}</div>
        </div>
        {active?.n ? (
          <span className="pm-badge inline-flex items-center gap-1" style={{ background: "var(--primary)", color: "white" }} data-testid={`bank-active-${s.section_id}`}>
            <Loader2 size={12} className="animate-spin" /> {active.n} building
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 text-center">
        <div><div className="font-mono text-2xl font-bold">{s.total}</div><div className="text-[10px]" style={{ color: "var(--text-muted)" }}>passages / {s.goal_passages}</div></div>
        <div><div className="font-mono text-2xl font-bold">{s.discrete}</div><div className="text-[10px]" style={{ color: "var(--text-muted)" }}>standalone / {s.goal_discrete}</div></div>
        <div>
          <div className="font-mono text-2xl font-bold">{w.pass_rate != null ? `${Math.round(w.pass_rate * 100)}%` : "—"}</div>
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>24h pass rate</div>
        </div>
      </div>
      <TypeBar by_type={s.by_type} total={s.total} />
      <div className="text-xs mt-3 flex justify-between" style={{ color: "var(--text-muted)" }}>
        <span>24h: {w.generated || 0} published · {w.rejected || 0} rejected · {w.aborted || 0} aborted</span>
        {needed > 0 && <span style={{ color: "var(--weakness)" }}>{needed} to goal</span>}
      </div>
      <div className="text-xs mt-1" style={{ color: s.calc_stock >= s.calc_goal ? "var(--mastery)" : "var(--text-muted)" }} data-testid={`bank-calc-stock-${s.section_id}`}>
        Unused calculation passages: <b className="font-mono">{s.calc_stock}</b> / {s.calc_goal} (topped up overnight)
      </div>
    </div>
  );
}

export default function BankHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/bank/health").then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  if (loading && !data) return <div className="py-16 text-center" data-testid="bank-loading"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return null;
  return (
    <div data-testid="bank-health">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold">Science passage bank</h2>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>Last {data.window_hours} h. Updates every 30 s.</p>
        </div>
        <button onClick={load} className="pm-btn-secondary inline-flex items-center gap-1.5 text-sm" data-testid="bank-refresh"><RefreshCw size={13} /> Refresh</button>
      </div>
      <SpendLimit />
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {data.sections.map((s) => <SectionCard key={s.section_id} s={s} active={data.active_generation[s.section_id]} />)}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="pm-card p-5" data-testid="bank-rejections">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><AlertTriangle size={15} /> Top rejection reasons · 24h</div>
          {data.rejections_top.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>No rejections logged in this window.</p>}
          {data.rejections_top.map((r) => (
            <div key={r.reason} className="flex justify-between items-start gap-3 text-sm py-1.5 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid="bank-rejection-row">
              <span className="flex-1">{r.reason}</span>
              <span className="font-mono text-xs shrink-0" style={{ color: "var(--text-muted)" }}>{r.count}× · {r.last_section}</span>
            </div>
          ))}
        </div>
        <div className="pm-card p-5" data-testid="bank-events">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><Clock size={15} /> Recent events</div>
          {data.recent_events.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>Quiet. No generation activity.</p>}
          <div className="max-h-80 overflow-y-auto">
            {data.recent_events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                {e.kind === "generated" ? <CheckCircle2 size={14} style={{ color: "var(--mastery)" }} />
                  : e.kind === "rejected" ? <AlertTriangle size={14} style={{ color: "#e08a2a" }} />
                    : <AlertTriangle size={14} style={{ color: "var(--weakness)" }} />}
                <span className="font-mono text-xs w-14 shrink-0" style={{ color: "var(--text-muted)" }}>{e.section_id} · {e.passage_type || "?"}</span>
                <span className="flex-1 truncate" title={(e.reasons || []).join(" · ")}>{e.kind === "generated" ? (e.passage_id || "published") : (e.reasons || [])[0] || e.kind}</span>
                <span className="text-xs shrink-0" style={{ color: "var(--text-muted)" }}>{dt(e.at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
